import React from "react";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  Title,
  LineController,
  BarController,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  BarController,
  LineController,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
  Title,
  annotationPlugin
);

const monthMap = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const FloatInventoryTrend = ({ data = [] }) => {
  const convertToCrores = (costInRupees) => costInRupees / 10000000;

  const sortedData = data
    ?.filter((item) => item && item.month && item.year)
    ?.sort((a, b) => a.year - b.year || a.month - b.month)
    ?.map((item) => ({
      label: `${monthMap[item.month]} ${item.year.toString().slice(-2)}`,
      count: item.count || 0,
      cost: convertToCrores(item.cost),
    }));

  const labels = sortedData.map((d) => d.label);
  const counts = sortedData.map((d) => d.count);
  const costs = sortedData.map((d) => d.cost);

  const getTrendline = (yValues) => {
    if (!yValues.length) return [];
    const xValues = yValues.map((_, i) => i + 1);
    const n = yValues.length;
    const xMean = xValues.reduce((a, b) => a + b, 0) / n;
    const yMean = yValues.reduce((a, b) => a + b, 0) / n;
    const numerator = xValues.reduce(
      (acc, x, i) => acc + (x - xMean) * (yValues[i] - yMean),
      0
    );
    const denominator = xValues.reduce((acc, x) => acc + (x - xMean) ** 2, 0);
    const m = denominator !== 0 ? numerator / denominator : 0;
    const c = yMean - m * xMean;
    return xValues.map((x) => +(m * x + c));
  };

  const trendline = getTrendline(costs);

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Engine Count",
        data: counts,
        backgroundColor: "#73b5d4",
        yAxisID: "y1",
        barThickness: 40, // Reduced bar thickness
      },
      {
        type: "line",
        label: "Cost (in Crores)",
        data: costs,
        borderColor: "black",
        backgroundColor: "black",
        fill: false,
        tension: 0.4,
        yAxisID: "y2",
        pointRadius: 4,
        pointHoverRadius: 5,
        zIndex: 100, // Ensure the line is on top of the bars
      },
      {
        type: "line",
        label: "Trendline (Cost)",
        data: trendline,
        borderColor: "red",
        borderDash: [5, 5],
        fill: false,
        tension: 0,
        yAxisID: "y2",
        pointRadius: 0,
        zIndex: 15, // Ensure the trendline is on top of the line chart
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
      datalabels: {
        display: false,
      },
      annotation: {
        annotations: {
          targetLine: {
            type: "line",
            yMin: 30,
            yMax: 30,
            borderColor: "green",
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              content: "Target: 30 Engines",
              enabled: true,
              position: "start",
              color: "green",
              font: { weight: "bold" }, // Optional: Make label bold
            },
            yScaleID: "y1",
          },
        },
      },
    },
    scales: {
      y1: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: { display: true, text: "Engine Count" },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y2: {
        type: "linear",
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Inventory Value (Crores)" },
        grid: { drawOnChartArea: true },
      },
      x: {
        title: { display: true, text: "Month" },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", height: "400px" }}>
      {labels.length ? (
        <Chart type="bar" data={chartData} options={chartOptions} />
      ) : (
        <div
          style={{ textAlign: "center", paddingTop: "100px", color: "#777" }}
        >
          No data available to display.
        </div>
      )}
    </div>
  );
};

export default FloatInventoryTrend;
